import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '@kidshome/i18n';
import { resolveErrorMessage, type OccurrenceResponse } from '@kidshome/api-contract';
import { Button } from '@kidshome/ui';
import { ActionSheet } from '@/shared/ui';
import { isGuardian, useAuthStore } from '@/entities/session';
import { useHouseholdContext } from '@/shared/lib/household';
import { parseLocalDate } from '@/shared/lib/dates';
import { tDynamic } from '@/shared/lib/i18n';
import { PhotoCapture } from '@/features/upload-photo-proof';
import { useBackfillTaskAction } from '@/features/backfill-task/use-backfill-task';

/**
 * `BackfillTask` — the kind "Forgot to check this off? Log it for {{day}}" affordance (ADR-0040) that
 * replaces a dead "MISSED" state on a `missed` (or planned-but-past) chore. The day is the occurrence's
 * own `scheduledLocalDate`, formatted in the household timezone — never shaming, just "log the day you
 * really did it".
 *
 * Tapping opens a small confirm sheet. A photo-proof chore captures a FRESH photo first (the same
 * `PhotoCapture` flow `complete` uses) → backfill with the key; a plain chore confirms → backfill. On
 * success we show the routed result kindly: a guardian's no-approval log lands done ("Logged! 🎉"); a
 * child's request (or any approval-gated chore) is "Sent to a grown-up to approve". A window-expired
 * error surfaces the gentle "ask a grown-up" message; the server owns the hard ~7-day bound.
 */
export function BackfillTask({
  occurrence,
  childId,
  onDone,
  compact = false,
}: {
  occurrence: OccurrenceResponse;
  /** The child whose balance a guardian-approved backfill credits (defaults to the assignee). */
  childId?: string;
  onDone?: () => void;
  /** Render a small round icon trigger (for a compact list row) instead of a full-width button. */
  compact?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  const { backfill, isPending, isDone, error, reset } = useBackfillTaskAction({
    date: occurrence.scheduledLocalDate,
    assigneeId: occurrence.assigneeId,
    childId,
  });

  const viewerIsGuardian = !!user && isGuardian(user);
  // A back-dated day is a civil date at UTC noon (parseLocalDate) — format it in 'UTC', not the
  // household timeZone, or a UTC+12-or-later household labels the confirmation UI a day forward.
  const dayLabel = formatDay(locale, parseLocalDate(occurrence.scheduledLocalDate), 'UTC');
  // A GUARDIAN's log is authoritative (the domain exempts them from photo proof + auto-approves), so a
  // grown-up never has to hunt for the kid's camera — only the KID's own back-date still needs a photo.
  const needsPhoto = occurrence.requiresPhotoProof && !viewerIsGuardian;
  // A guardian's log is authoritative → credited right away; a child's request goes to a grown-up first.
  const landsDone = viewerIsGuardian;

  const errorMessage =
    error != null
      ? (() => {
          const m = resolveErrorMessage(error);
          return tDynamic(t, m.key, m.params);
        })()
      : null;

  const close = (): void => {
    reset();
    setOpen(false);
    if (isDone) onDone?.();
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('backfill.cta', { day: dayLabel })}
          title={t('backfill.cta', { day: dayLabel })}
          className="flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/40 text-primary outline-none transition-colors hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
            {/* a "log it back" glyph: a check with a small back-arrow feel via a curved stroke */}
            <path
              d="M4 12a8 8 0 1 0 2.3-5.6M4 4v3h3"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.5 12.5l2 2 3.5-4"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : (
        <Button variant="secondary" size="lg" className="w-full" onClick={() => setOpen(true)}>
          {t('backfill.cta', { day: dayLabel })}
        </Button>
      )}
      <ActionSheet
        open={open}
        onClose={close}
        title={t('backfill.title', { day: dayLabel })}
        description={t('backfill.kind')}
        footer={
          isDone ? (
            <Button variant="secondary" onClick={close}>
              {t('backfill.resultDismiss')}
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              {/* A photo-proof chore confirms THROUGH the capture (fresh photo → backfill with the
                  key); a plain chore gets a direct confirm button. */}
              {!needsPhoto && (
                <Button
                  variant="secondary"
                  disabled={isPending}
                  onClick={() => backfill(occurrence.id)}
                >
                  {t('backfill.confirm', { day: dayLabel })}
                </Button>
              )}
              <Button variant="ghost" onClick={close}>
                {t('backfill.cancel')}
              </Button>
            </div>
          )
        }
      >
        {isDone ? (
          <p role="status" className="text-center text-sm font-semibold text-text">
            {landsDone ? t('backfill.resultLogged') : t('backfill.resultPending')}
          </p>
        ) : errorMessage ? (
          <p role="alert" className="text-center text-sm font-semibold text-danger">
            {errorMessage}
          </p>
        ) : needsPhoto ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-muted">{t('backfill.photoHint')}</p>
            <PhotoCapture
              occurrenceId={occurrence.id}
              onUploaded={(photoProofKey) => backfill(occurrence.id, photoProofKey)}
            />
          </div>
        ) : (
          <p className="text-sm text-text-muted">{t('backfill.confirmBody', { day: dayLabel })}</p>
        )}
      </ActionSheet>
    </>
  );
}
